import React, { useState, useRef, useEffect } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '../common/Dropdown';
import type {
  AdditionalActivity,
  AdditionalActivitiesWidgetLabels,
} from '../../shared/types/additional-activities';

export interface AdditionalActivitiesWidgetProps {
  activities: AdditionalActivity[];
  suggestions: string[];
  manageUrl: string;
  onAddActivity: (name: string) => void;
  onRemoveActivity: (activityId: string) => void;
  isAdding?: boolean;
  labels: AdditionalActivitiesWidgetLabels;
}

export function AdditionalActivitiesWidget({
  activities,
  suggestions,
  manageUrl,
  onAddActivity,
  onRemoveActivity,
  isAdding = false,
  labels,
}: AdditionalActivitiesWidgetProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = suggestions.filter((s) =>
        s.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, suggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (name: string = inputValue) => {
    const trimmedName = name.trim();
    if (trimmedName) {
      onAddActivity(trimmedName);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSubmit(suggestion);
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">
            <i className="bi bi-tags me-2"></i>
            {labels.title}
          </h5>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-dark p-0">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem href={manageUrl}>
                <i className="bi bi-gear me-2"></i>
                {labels.manage}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>

        <div className="mb-3">
          {/* Activity tags */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {activities.map((activity) => (
              <span
                key={activity.id}
                className="badge bg-secondary d-flex align-items-center gap-1"
                style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
              >
                <i className="bi bi-tag-fill"></i>
                {activity.name}
                <button
                  type="button"
                  className="btn btn-link p-0 text-white ms-1"
                  style={{ lineHeight: 1, textDecoration: 'none' }}
                  onClick={() => onRemoveActivity(activity.id)}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: '0.75rem' }}></i>
                </button>
              </span>
            ))}
          </div>

          {activities.length === 0 && (
            <p className="text-muted small mb-3">{labels.noActivities}</p>
          )}

          {/* Input field */}
          <div className="position-relative" style={{ minWidth: 200 }}>
            <input
              ref={inputRef}
              type="text"
              className="form-control pe-5"
              placeholder={`${labels.addActivity}...`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (filteredSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              disabled={isAdding}
              autoComplete="off"
            />
            <button
              className="btn btn-primary btn-sm position-absolute end-0 top-0 bottom-0 me-1 my-1"
              type="button"
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              onClick={() => handleSubmit()}
              disabled={isAdding || !inputValue.trim()}
            >
              {isAdding ? (
                <span className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
              ) : (
                <i className="bi bi-plus-lg"></i>
              )}
            </button>

            {/* Suggestions dropdown */}
            {showSuggestions && (
              <div
                ref={suggestionsRef}
                className="list-group position-absolute w-100 mt-1 shadow-sm"
                style={{ zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}
              >
                {filteredSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <i className="bi bi-tag me-2 text-muted"></i>
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdditionalActivitiesWidget;
